import React, { useState } from 'react';
import { Button } from '../../ui';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import CountrySelector from '../CountrySelector';
import { UnitSystem } from '../UnitSystem';
import ParcelList from '../ParcelList';
import DocumentList from './DocumentList';
import AuthDialog from './AuthDialog';
import { Parcel } from '../utils/calculationUtils';
import countriesByContinent from '../data/countryData';
import { useAuth } from '../../../context/AuthContext';
import { Icon } from '../../ui/Icon';

// Shipping type
export type ShippingType = 'package' | 'document';

// Document interface
export interface Document {
  id: string;
  quantity: number;
  weight: number;
}

const shippingFormSchema = z.object({
  originCountry: z.string().min(1, 'Origin country is required'),
  destinationCountry: z.string().min(1, 'Destination country is required'),
  originPostalCode: z.string().max(10, 'Postal code must be 10 characters or less').optional(),
  destinationPostalCode: z.string().max(10, 'Postal code must be 10 characters or less').optional(),
});

type ShippingFormValues = z.infer<typeof shippingFormSchema>;

interface ShippingFormProps {
  isCollapsed: boolean;
  userOriginCountry: string;
  userDestinationCountry: string;
  unitSystem: UnitSystem;
  shippingType: ShippingType;
  shipmentType: 'international' | 'domestic';
  parcels: Parcel[];
  documents: Document[];
  isRTL: boolean;
  isLoading: boolean;
  originPostalCode: string;
  destinationPostalCode: string;
  onUnitSystemChange: (system: UnitSystem) => void;
  onShippingTypeChange: (event: React.MouseEvent<HTMLElement>, newType: ShippingType | null) => void;
  onShipmentTypeChange: (type: 'international' | 'domestic') => void;
  onAddParcel: () => void;
  onRemoveParcel: (id: string) => void;
  onUpdateParcel: (id: string, field: string, value: number) => void;
  onAddDocument: () => void;
  onRemoveDocument: (id: string) => void;
  onUpdateDocument: (id: string, field: string, value: any) => void;
  onSwapLocations: () => void;
  onOriginPostalCodeChange: (code: string) => void;
  onDestinationPostalCodeChange: (code: string) => void;
  onSubmit: (data: ShippingFormValues) => void;
}

const ShippingForm: React.FC<ShippingFormProps> = ({
  isCollapsed,
  userOriginCountry = '',
  userDestinationCountry = '',
  unitSystem,
  shippingType,
  parcels,
  documents,
  isLoading,
  originPostalCode,
  destinationPostalCode,
  onUnitSystemChange,
  onShippingTypeChange,
  onAddParcel,
  onRemoveParcel,
  onUpdateParcel,
  onAddDocument,
  onRemoveDocument,
  onUpdateDocument,
  onSwapLocations,
  onOriginPostalCodeChange,
  onDestinationPostalCodeChange,
  onSubmit,
}) => {
  const { isAuthenticated } = useAuth();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
  } = useForm<ShippingFormValues>({
    resolver: zodResolver(shippingFormSchema),
    defaultValues: {
      originCountry: userOriginCountry,
      destinationCountry: userDestinationCountry,
      originPostalCode: originPostalCode,
      destinationPostalCode: destinationPostalCode,
    },
    mode: 'onChange',
  });

  const watchedValues = watch();

  React.useEffect(() => {
    setValue('originPostalCode', originPostalCode);
    setValue('destinationPostalCode', destinationPostalCode);
  }, [setValue, originPostalCode, destinationPostalCode]);

  const handlePostalCodeChange = (type: 'origin' | 'destination', value: string) => {
    if (type === 'origin') onOriginPostalCodeChange(value);
    else onDestinationPostalCodeChange(value);
  };

  const handleFormSubmit = (data: ShippingFormValues) => {
    if (!isAuthenticated) {
      setAuthDialogOpen(true);
      return;
    }
    onSubmit(data);
    window.scrollTo(0, 0);
  };

  // Synthetic event for the existing onShippingTypeChange signature.
  const handleTypeCardClick = (type: ShippingType) => {
    if (shippingType === type) return;
    const synthetic = { preventDefault: () => {}, stopPropagation: () => {} } as unknown as
      React.MouseEvent<HTMLElement>;
    onShippingTypeChange(synthetic, type);
  };

  // Aggregate weight calculations for the bottom total readout.
  const wUnit = unitSystem === 'metric' ? 'kg' : 'lb';
  const items = shippingType === 'package' ? parcels : documents;
  const totalActual = items.reduce((sum, item: any) => {
    const qty = item.quantity ?? 1;
    const w = item.physicalWeight ?? item.weight ?? 0;
    return sum + w * qty;
  }, 0);
  const totalVolumetric = items.reduce((sum, item: any) => {
    const qty = item.quantity ?? 1;
    const v = item.dimensionalWeight ?? 0;
    return sum + v * qty;
  }, 0);
  const totalBillable = Math.max(totalActual, totalVolumetric);

  const isFormReady = () =>
    isValid &&
    ((shippingType === 'package' && parcels.length > 0) ||
      (shippingType === 'document' && documents.length > 0));
  const itemsCount = shippingType === 'package' ? parcels.length : documents.length;
  const itemsLabel = shippingType === 'package' ? 'PARCELS' : 'DOCUMENTS';

  // ── Compact / collapsed mode (used on the Rates / Address steps) ──────────
  if (isCollapsed) {
    const originName =
      countriesByContinent[Object.keys(countriesByContinent)[0]]?.find(
        (c: any) => c.code === watchedValues.originCountry
      )?.name ||
      Object.values(countriesByContinent)
        .flat()
        .find((c: any) => c.code === watchedValues.originCountry)?.name ||
      watchedValues.originCountry ||
      'Origin';
    const destName =
      Object.values(countriesByContinent)
        .flat()
        .find((c: any) => c.code === watchedValues.destinationCountry)?.name ||
      watchedValues.destinationCountry ||
      'Destination';

    const firstParcel = (parcels[0] as any) || {};
    const parcelDims =
      firstParcel.length && firstParcel.width && firstParcel.height
        ? `${firstParcel.length}×${firstParcel.width}×${firstParcel.height} ${
            unitSystem === 'metric' ? 'cm' : 'in'
          }`
        : '—';
    const itemsCount = shippingType === 'package' ? parcels.length : documents.length;
    const itemsLabel =
      shippingType === 'package'
        ? `${itemsCount} parcel${itemsCount !== 1 ? 's' : ''}`
        : `${itemsCount} document${itemsCount !== 1 ? 's' : ''}`;

    return (
      <form onSubmit={handleSubmit(handleFormSubmit)} id="shipping-calculator-form">
        <div
          className="card card-pad"
          style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          {/* Route summary */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Icon name="mapPin" size={16} color="var(--brand-blue)" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="ship-kicker" style={{ marginBottom: 2 }}>Route</div>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 14,
                  color: 'var(--ink)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {originName}{' '}
                <Icon name="arrowRight" size={12} color="var(--ink-3)" />{' '}
                {destName}
              </div>
            </div>
          </div>

          <div className="divider" />

          {/* Parcels summary */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Icon
              name={shippingType === 'package' ? 'box' : 'file'}
              size={16}
              color="var(--brand-blue)"
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="ship-kicker" style={{ marginBottom: 2 }}>
                {shippingType === 'package' ? 'Package' : 'Document'}
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                <span className="mono" style={{ fontWeight: 600 }}>
                  {totalBillable.toFixed(2)} {wUnit}
                </span>{' '}
                · {itemsLabel} · <span className="mono">{parcelDims}</span>
              </div>
            </div>
          </div>

          <div className="divider" />

          {/* Quick country swap inline (no labels — keep it tight) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Controller
              name="originCountry"
              control={control}
              render={({ field }) => (
                <CountrySelector
                  id="origin-country-collapsed"
                  label="From"
                  value={field.value}
                  onChange={field.onChange}
                  countries={countriesByContinent}
                  error={!!errors.originCountry}
                  helperText={errors.originCountry?.message}
                />
              )}
            />
            <Controller
              name="destinationCountry"
              control={control}
              render={({ field }) => (
                <CountrySelector
                  id="destination-country-collapsed"
                  label="To"
                  value={field.value}
                  onChange={field.onChange}
                  countries={countriesByContinent}
                  error={!!errors.destinationCountry}
                  helperText={errors.destinationCountry?.message}
                />
              )}
            />
          </div>

          <Button
            type="submit"
            variant="blue"
            block
            disabled={isLoading || !isFormReady()}
          >
            <Icon name="refresh" size={14} />
            <span>{isLoading ? 'Updating quotes…' : 'Get new quotes'}</span>
          </Button>

          <p className="muted" style={{ fontSize: 11, textAlign: 'center', margin: 0 }}>
            Need to change weight or dimensions? Go back to step 1 to edit the full form.
          </p>
        </div>
        <AuthDialog
          open={authDialogOpen}
          onClose={() => setAuthDialogOpen(false)}
          onSuccess={() => {
            setAuthDialogOpen(false);
            onSubmit({
              originCountry: control._formValues.originCountry,
              destinationCountry: control._formValues.destinationCountry,
              originPostalCode: control._formValues.originPostalCode,
              destinationPostalCode: control._formValues.destinationPostalCode,
            });
          }}
        />
      </form>
    );
  }

  // ── Full wizard layout ────────────────────────────────────────
  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} id="shipping-calculator-form">
      <div className="ship-sheet">
        {/* 01. Shipment Type */}
        <section className="ship-row">
          <header className="ship-row-head">
            <span className="ship-num">01</span>
            <div>
              <h3>Shipment type</h3>
              <p>Cross-border or within country.</p>
            </div>
          </header>
          <div className="ship-row-body">
            <div className="ship-type-grid">
              <button
                type="button"
                className="ship-type-card active"
                onClick={() => {}}
                aria-pressed
              >
                <Icon name="globe" size={18} />
                <div className="ship-type-label">Send internationally</div>
                <span className="ship-type-status">Available</span>
              </button>
              <button
                type="button"
                className="ship-type-card"
                disabled
                title="Domestic shipping is coming soon"
              >
                <Icon name="home" size={18} />
                <div className="ship-type-label">Send domestically</div>
                <span className="ship-type-status soon">Coming soon</span>
              </button>
            </div>
          </div>
        </section>

        {/* 02. Route */}
        <section className="ship-row">
          <header className="ship-row-head">
            <span className="ship-num">02</span>
            <div>
              <h3>Route</h3>
              <p>Where it goes from, where it ends up.</p>
            </div>
          </header>
          <div className="ship-row-body">
            <div className="ship-locations">
              <div className="ship-loc-col">
                <div className="ship-loc-label">
                  <span className="ship-loc-node origin" /> From
                </div>
                <Controller
                  name="originCountry"
                  control={control}
                  render={({ field }) => (
                    <CountrySelector
                      id="origin-country"
                      label="Origin country"
                      value={field.value}
                      onChange={field.onChange}
                      countries={countriesByContinent}
                      error={!!errors.originCountry}
                      helperText={errors.originCountry?.message}
                    />
                  )}
                />
                <Controller
                  name="originPostalCode"
                  control={control}
                  render={({ field }) => (
                    <div className="field">
                      <label className="field-label">
                        Postal code <span className="muted">· optional</span>
                      </label>
                      <input
                        type="text"
                        className="input"
                        placeholder="e.g. 00000"
                        value={field.value || ''}
                        onChange={(e) => {
                          field.onChange(e);
                          handlePostalCodeChange('origin', e.target.value);
                        }}
                        style={errors.originPostalCode ? { borderColor: 'var(--err)' } : undefined}
                      />
                      {errors.originPostalCode && (
                        <span style={{ fontSize: 11, color: 'var(--err)' }}>
                          {errors.originPostalCode.message}
                        </span>
                      )}
                    </div>
                  )}
                />
              </div>

              <div className="ship-swap-wrap">
                <div className="ship-route-line" />
                <button
                  type="button"
                  className="ship-swap"
                  onClick={() => {
                    const origCountry = control._formValues.originCountry;
                    const origPostal = control._formValues.originPostalCode;
                    setValue('originCountry', control._formValues.destinationCountry);
                    setValue('originPostalCode', control._formValues.destinationPostalCode);
                    setValue('destinationCountry', origCountry);
                    setValue('destinationPostalCode', origPostal);
                    onSwapLocations();
                  }}
                  title="Swap origin & destination"
                  aria-label="Swap origin and destination"
                >
                  <Icon name="arrowRight" size={13} />
                </button>
              </div>

              <div className="ship-loc-col">
                <div className="ship-loc-label">
                  <span className="ship-loc-node dest" /> To
                </div>
                <Controller
                  name="destinationCountry"
                  control={control}
                  render={({ field }) => (
                    <CountrySelector
                      id="destination-country"
                      label="Destination country"
                      value={field.value}
                      onChange={field.onChange}
                      countries={countriesByContinent}
                      error={!!errors.destinationCountry}
                      helperText={errors.destinationCountry?.message}
                    />
                  )}
                />
                <Controller
                  name="destinationPostalCode"
                  control={control}
                  render={({ field }) => (
                    <div className="field">
                      <label className="field-label">
                        Postal code <span className="muted">· optional</span>
                      </label>
                      <input
                        type="text"
                        className="input"
                        placeholder="e.g. 00000"
                        value={field.value || ''}
                        onChange={(e) => {
                          field.onChange(e);
                          handlePostalCodeChange('destination', e.target.value);
                        }}
                        style={errors.destinationPostalCode ? { borderColor: 'var(--err)' } : undefined}
                      />
                      {errors.destinationPostalCode && (
                        <span style={{ fontSize: 11, color: 'var(--err)' }}>
                          {errors.destinationPostalCode.message}
                        </span>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>
          </div>
        </section>

        {/* 03. Contents (item type + units) */}
        <section className="ship-row">
          <header className="ship-row-head">
            <span className="ship-num">03</span>
            <div>
              <h3>Contents</h3>
              <p>What's inside, and how you measure it.</p>
            </div>
          </header>
          <div className="ship-row-body">
            <div className="ship-contents-grid">
              <div>
                <div className="field-label">Item type</div>
                <div className="ship-what-grid">
                  <button
                    type="button"
                    className={`ship-what-card ${shippingType === 'package' ? 'active' : ''}`}
                    onClick={() => handleTypeCardClick('package')}
                    aria-pressed={shippingType === 'package'}
                  >
                    <Icon name="box" size={20} />
                    <div className="ship-what-title">Package</div>
                    <div className="ship-what-sub">Parcels, boxes</div>
                  </button>
                  <button
                    type="button"
                    className={`ship-what-card ${shippingType === 'document' ? 'active' : ''}`}
                    onClick={() => handleTypeCardClick('document')}
                    aria-pressed={shippingType === 'document'}
                  >
                    <Icon name="file" size={20} />
                    <div className="ship-what-title">Document</div>
                    <div className="ship-what-sub">Letters, papers</div>
                  </button>
                </div>
              </div>
              <div>
                <div className="field-label">Units</div>
                <div className="seg-lg">
                  <button
                    type="button"
                    className={unitSystem === 'metric' ? 'active' : ''}
                    onClick={() => onUnitSystemChange('metric')}
                  >
                    Metric <span className="mono muted">kg · cm</span>
                  </button>
                  <button
                    type="button"
                    className={unitSystem === 'imperial' ? 'active' : ''}
                    onClick={() => onUnitSystemChange('imperial')}
                  >
                    Imperial <span className="mono muted">lb · in</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 04. Items (Parcels or Documents) */}
        <section className="ship-row">
          <header className="ship-row-head">
            <span className="ship-num">04</span>
            <div>
              <h3>{shippingType === 'package' ? 'Parcels' : 'Documents'}</h3>
              <p>
                {shippingType === 'package'
                  ? 'Add every box going in this shipment.'
                  : 'Add each envelope or document set.'}
              </p>
            </div>
          </header>
          <div className="ship-row-body">
            {shippingType === 'package' ? (
              <ParcelList
                parcels={parcels}
                unitSystem={unitSystem}
                onAddParcel={onAddParcel}
                onRemoveParcel={onRemoveParcel}
                onUpdateParcel={onUpdateParcel}
              />
            ) : (
              <DocumentList
                documents={documents}
                unitSystem={unitSystem}
                onAddDocument={onAddDocument}
                onRemoveDocument={onRemoveDocument}
                onUpdateDocument={onUpdateDocument}
              />
            )}
          </div>
        </section>

        {/* Total readout */}
        <section className="ship-total">
          <div className="ship-total-left">
            <span className="ship-kicker">Billable weight</span>
            <div className="ship-total-value">
              {totalBillable.toFixed(2)} <span>{wUnit}</span>
            </div>
            <div className="muted" style={{ fontSize: 12 }}>
              Actual{' '}
              <strong className="mono">
                {totalActual.toFixed(2)} {wUnit}
              </strong>{' '}
              ·  Volumetric{' '}
              <strong className="mono">
                {totalVolumetric.toFixed(2)} {wUnit}
              </strong>
            </div>
          </div>
          <div className="ship-total-right">
            <div className="ship-total-items">
              <span className="ship-kicker">{itemsLabel}</span>
              <span className="ship-total-items-val">{itemsCount}</span>
            </div>
            <Button type="submit" variant="blue" size="lg" disabled={isLoading || !isFormReady()}>
              {isLoading ? 'Getting quotes…' : 'Get shipping quotes'}
              <Icon name="arrowRight" size={15} />
            </Button>
          </div>
        </section>
      </div>

      <AuthDialog
        open={authDialogOpen}
        onClose={() => setAuthDialogOpen(false)}
        onSuccess={() => {
          setAuthDialogOpen(false);
          onSubmit({
            originCountry: control._formValues.originCountry,
            destinationCountry: control._formValues.destinationCountry,
            originPostalCode: control._formValues.originPostalCode,
            destinationPostalCode: control._formValues.destinationPostalCode,
          });
        }}
      />

      {/* Reference watched values to keep react-hook-form happy when collapsed elsewhere */}
      <input type="hidden" value={String(watchedValues.originCountry || '')} readOnly hidden />
    </form>
  );
};

export default ShippingForm;
